import Booking, { calculateCourtNumbers } from './Booking'
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { differenceInMinutes, isWeekend, parse } from 'date-fns'
import {
  getBookingScheduleInfo,
  getDisplayDate,
  getMaxBookingTime,
  isBallMachineAvailable,
  isTimeslotBookable,
  mayBeBooked,
} from '../../util/bookingUtil'
import { hideNotification, notifyError, notifyWarn } from '../../util/notifyUtil'

import AppContext from '../common/AppContext'
import { Spin } from 'antd'
import TimeMarker from './TimeMarker'
import axios from '../../axios/axios'
import { formatDateTime } from '../../util/dateTimeUtil'
import useResponsive from '../../hooks/useResponsive'
import { useWindowSize } from '../../hooks/useWindowSize'

const GRID_TEMPLATE_ROW_HEIGHT = 25
const TIME_OFFSET = 10 - GRID_TEMPLATE_ROW_HEIGHT / 2 // This centers the time label vertically on the row
export const ROW_HEIGHT = GRID_TEMPLATE_ROW_HEIGHT * 2 + 3 // The total row height
export const ROW_GAP = 4 // The gap between rows

const formatDisplayTime = (index, SLOT_DURATION, START_HOUR) => {
  return formatTime(index, SLOT_DURATION, START_HOUR, true)
}

const formatTime = (index, SLOT_DURATION, START_HOUR, forDisplay = false) => {
  // 1. Calculate total minutes elapsed from the very start
  const totalMinutes = index * SLOT_DURATION

  // 2. Calculate current hour and remaining minutes
  let hour = Math.floor(totalMinutes / 60) + START_HOUR
  const min = totalMinutes % 60

  let AMPM = ''
  if (forDisplay) {
    // Optional: Handle PM transition if needed
    // AMPM = hour >= 12 ? 'pm' : 'am';

    // Convert to 12-hour format
    if (hour > 12) hour = hour - 12
    if (hour === 0) hour = 12 // Handle midnight if applicable
  }

  let hourStr = hour.toString()
  let minStr = min.toString()
  if (forDisplay) {
    // 3. Format strings to ensure leading zeros (e.g., 08:05)
    // hourStr = hourStr.padStart(2, '0')
    minStr = minStr.padStart(2, '0')
  }

  return `${hourStr}:${minStr}${AMPM}`
}

const TimeLabel = memo(({ rowIndex, gridColumn = 1, date, selection, isDragging, hoveredRow }) => {
  const { START_HOUR, SLOT_DURATION } = getBookingScheduleInfo(date)
  const isTimeslotInPast = (rowIndex) => {
    const time = parse(formatTime(rowIndex, SLOT_DURATION, START_HOUR), 'HH:mm', date)
    return !isTimeslotBookable(time)
  }

  const isLeft = gridColumn === 1
  const isPast = isTimeslotInPast(rowIndex)

  const { startRow, endRow } = selection ?? {}
  const isSelected =
    (!isDragging && hoveredRow === rowIndex) ||
    (isDragging && rowIndex >= Math.min(startRow, endRow) && rowIndex <= Math.max(startRow, endRow) + 1)

  let timeString = formatDisplayTime(rowIndex, SLOT_DURATION, START_HOUR)

  const color = isPast ? '#bbb' : isSelected && isDragging ? '#000' : '#333'

  return (
    <div
      style={{
        fontSize: '12px',
        gridColumn,
        gridRow: rowIndex + 2,
        position: 'relative',
        top: `${TIME_OFFSET}px`,
        transition: 'all 0.2s',
        paddingRight: '8px',
        display: 'flex', // Use flex to control alignment
        alignItems: 'center', // Centers content vertically in the row
        justifyContent: 'flex-end',
        borderRadius: '4px 0 0 4px', // Optional: rounded left edge for a cleaner look
      }}
    >
      <span
        style={{
          position: 'relative',
          top: '-0.8em', // Only the text shifts up to "sit" on the line
          color: color,
          paddingLeft: isLeft ? 0 : '20px',
          paddingRight: !isLeft ? 0 : '10px',
          borderRadius: '50px',
          // textDecoration: isTimeslotInPast(rowIndex) ? 'line-through' : '',
          // fontStyle: isEvenHour ? 'normal' : 'italic',
          fontWeight: isSelected ? 'bold' : 'normal',
          // fontSize: isEvenHour ? '12px' : '10px',
        }}
      >
        {/* {formatDisplayTime(rowIndex)}({rrgb(102, 116, 129)}) */}
        {timeString}
      </span>
    </div>
  )
})

const CourtSchedule = ({
  date,
  onAdd,
  onEdit,
  onDelete,
  maxBookingTime,
  bookingTimeRemaining,
  forceRefresh,
  isFirst = true,
  isLast = true,
  multiDay = false,
}) => {
  // Manually set a date for test purposes:
  // date = set(date, { hours: 12 + 5, minutes: 41 })
  // console.log('Rendering CourtSchedule for date', date)
  const bp = useResponsive()
  const { width } = useWindowSize()
  const [loading, setLoading] = useState([])
  const [bookings, setBookings] = useState([])
  const [selection, setSelection] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [hoveredRow, setHoveredRow] = useState(null)
  const [windowSizeWarningDisplayed, setWindowSizeWarningDisplayed] = useState(false)

  const { START_HOUR, END_HOUR, SLOT_DURATION } = getBookingScheduleInfo(date)

  const courts = [1, 2, 3]

  const getBookings = useCallback(async (date, showLoadingIndicator = true) => {
    setLoading(showLoadingIndicator)
    try {
      const result = await axios.get(`/all/bookings/${formatDateTime(date, 'yyyyMMdd')}`)
      setBookings(result?.data)
    } catch (e) {
      notifyError('error', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const fetchBookings = (showLoadingIndicator) => getBookings(date, showLoadingIndicator)
    // Fetch immediately, then every 30 seconds.
    fetchBookings(true)
    const interval = setInterval(() => fetchBookings(false), 30 * 1000)
    return () => clearInterval(interval)
  }, [date, getBookings])

  // Use a Ref to access the latest state inside the global event listener
  const selectionRef = useRef(selection)
  const isDraggingRef = useRef(isDragging)

  // Keep refs in sync with state
  useEffect(() => {
    selectionRef.current = selection
    isDraggingRef.current = isDragging
  }, [selection, isDragging])

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      // Only trigger if we were actually dragging
      if (isDraggingRef.current && selectionRef.current) {
        setIsDragging(false)
        setSelection(null)
      }
    }
    // Add listener to window
    window.addEventListener('mouseup', handleGlobalMouseUp)

    // Clean up listener on component unmount to prevent memory leaks
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, []) // Empty dependency array: runs once on mount

  const calculateRow = (timeString) => {
    const serverTime = formatDateTime(timeString, 'HH:mm')
    const [hours, minutes] = serverTime.split(':').map(Number)
    const totalMinutesFromStart = (hours - START_HOUR) * 60 + minutes
    return Math.floor(totalMinutesFromStart / SLOT_DURATION)
  }

  const handleMouseDown = (court, rowIndex, bookingAllowed) => {
    if (!bookingAllowed) return
    setIsDragging(true)
    setSelection({ court, startRow: rowIndex, endRow: rowIndex })
  }

  const handleMouseEnter = (court, rowIndex, bookingAllowed) => {
    setHoveredRow(rowIndex)
    if (!isDragging) return

    // Check if the NEWLY hovered cell is occupied
    if (isCellOccupied(court, rowIndex, bookings)) {
      return
    }

    // Check if there are any occupied cells BETWEEN start and current index
    const start = Math.min(selection.startRow, rowIndex)
    const end = Math.max(selection.startRow, rowIndex)
    const selectedDuration = end - start
    const allowedDuration = getMaxBookingTime(currentUser, maxBookingTime, bookingTimeRemaining, false) - 2

    if (selectedDuration > allowedDuration) {
      return
    }

    for (let i = start; i <= end; i++) {
      if (isCellOccupied(court, i, bookings)) return // Block the update
    }

    if (!bookingAllowed) return

    setSelection((prev) => ({ ...prev, endRow: rowIndex }))
  }

  const isCellOccupied = (court, rowIndex, bookings) => {
    return bookings.some((booking) => {
      const startIdx = calculateRow(booking.startTime)
      const endIdx = calculateRow(booking.endTime)

      // Check if the court matches and if the row index falls within the booking range
      const courtNumbers = calculateCourtNumbers(booking.courts)
      const isInCourt = courtNumbers.includes(court)
      const isInTimeRange = rowIndex >= startIdx && rowIndex < endIdx

      return isInCourt && isInTimeRange
    })
  }

  const handleMouseUp = () => {
    if (!isDragging) return
    setIsDragging(false)

    // Sort rows in case user dragged upwards
    const start = Math.min(selection.startRow, selection.endRow)
    const end = Math.max(selection.startRow, selection.endRow) + 1

    // time, court, duration
    const startTime = formatTime(start, SLOT_DURATION, START_HOUR)
    const endTime = formatTime(end, SLOT_DURATION, START_HOUR)
    const startDateTime = parse(startTime, 'HH:mm', date)
    const endDateTime = parse(endTime, 'HH:mm', date)
    const duration = differenceInMinutes(endDateTime, startDateTime)
    onAdd(startDateTime, selection.court, duration, bookings)
    setIsDragging(false)
    setSelection(null)
  }

  const getDateTime = (index, date) => {
    return parse(formatTime(index, SLOT_DURATION, START_HOUR), 'HH:mm', date)
  }

  const totalRows = (END_HOUR - START_HOUR) * (60 / SLOT_DURATION)

  const { currentUser } = useContext(AppContext)

  let status = 'notavailable'

  const showFirstTimeLabelCol = true // multiDay ? isFirst : true
  const showLastTimeLabelCol = multiDay ? isLast : !bp.xs

  if (multiDay) {
    if (width < 1300) {
      if (!windowSizeWarningDisplayed) {
        notifyWarn(
          'screen-too-small',
          'Warning: small screen',
          'This page is optimized for larger screens.  Controls and navigation might be cut off on small screens.',
          9999,
        )
        setWindowSizeWarningDisplayed(true)
      }
    } else {
      hideNotification('screen-too-small')
    }
  }

  const headingClassName = multiDay
    ? isWeekend(date)
      ? 'bookingDateHeadingMultiDay weekend'
      : 'bookingDateHeadingMultiDay'
    : 'bookingDateHeading'

  return (
    <>
      {multiDay && (
        <div className={headingClassName}>
          <div style={{ textAlign: 'center', fontWeight: 'bold' }}>{getDisplayDate(date)}</div>
        </div>
      )}
      <Spin spinning={loading} size="large" delay={300} style={{ zIndex: 11 }}>
        <div
          className="bookingDay"
          style={{
            display: 'grid',
            gridTemplateColumns: '3em repeat(3, 1fr)',
            gridTemplateRows: `30px repeat(${totalRows}, ${GRID_TEMPLATE_ROW_HEIGHT}px)`,
            columnGap: multiDay ? '5px' : 'min(0.9vw, 15px)',
            rowGap: ROW_GAP + 'px',
            background: 'none',
            padding: '0px',
            borderRadius: '0px',
            userSelect: 'none',
          }}
        >
          {/* 1. Header Row */}
          <div style={{ gridColumn: 1, gridRow: 1 }} className="courtName" />
          {courts.map((c) => (
            <div key={c} style={{ textAlign: 'center', fontWeight: 'bold' }} className="courtName">
              Court {c}
            </div>
          ))}
          <TimeMarker date={date} showEndDot={true} />

          {/* 2. Interactive Background Cells */}
          {Array.from({ length: totalRows }).map((_, rowIndex) => {
            const timeslotStartTime = getDateTime(rowIndex, date)
            const ballMachineStyle = isBallMachineAvailable(timeslotStartTime) ? 'ballMachine' : ''
            return (
              <React.Fragment key={rowIndex}>
                {showFirstTimeLabelCol && (
                  <TimeLabel
                    rowIndex={rowIndex}
                    date={date}
                    selection={selection}
                    isDragging={isDragging}
                    hoveredRow={hoveredRow}
                  />
                )}

                {courts.map((courtNum) => {
                  // Check if this specific cell is part of the current drag selection
                  const isSelected =
                    selection &&
                    selection.court === courtNum &&
                    rowIndex >= Math.min(selection.startRow, selection.endRow) &&
                    rowIndex <= Math.max(selection.startRow, selection.endRow)

                  let bookingAllowed = false
                  if (!currentUser || mayBeBooked(timeslotStartTime, currentUser, courtNum)) {
                    status = 'available'
                    bookingAllowed = true
                  }

                  return (
                    <div
                      key={`${rowIndex}-${courtNum}`}
                      onMouseDown={() => handleMouseDown(courtNum, rowIndex, bookingAllowed)}
                      onMouseEnter={() => handleMouseEnter(courtNum, rowIndex, bookingAllowed)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onMouseUp={handleMouseUp}
                      className={`bookingTimeslot ${status} ${ballMachineStyle} ${isSelected ? 'selected' : ''}`}
                      style={{
                        gridColumn: courtNum + 1,
                        gridRow: rowIndex + 2,
                        borderTop: '1px solid #f0f0f0',
                        backgroundColor: isSelected ? '#e2f3dc9c' : 'transparent',
                        // backgroundColor: isSelected ? '#e2f3dc9c' : hoveredRow === rowIndex ? '#eeeeee4a' : 'transparent',
                        userSelect: 'none', // stops text selection while dragging
                      }}
                    />
                  )
                })}
                {showLastTimeLabelCol && (
                  <TimeLabel
                    rowIndex={rowIndex}
                    gridColumn={5}
                    date={date}
                    selection={selection}
                    isDragging={isDragging}
                    hoveredRow={hoveredRow}
                  />
                )}
              </React.Fragment>
            )
          })}

          {/* Time label for the last timeslot end time */}
          <React.Fragment key={totalRows}>
            {showFirstTimeLabelCol && (
              <TimeLabel
                rowIndex={totalRows}
                date={date}
                selection={selection}
                isDragging={isDragging}
                hoveredRow={hoveredRow}
              />
            )}

            {/* Empty divs for the courts for the last timeslot */}
            {courts.map((courtNum) => {
              return (
                <div
                  key={`${totalRows}-${courtNum}`}
                  className={`bookingTimeslot ${status} `}
                  style={{
                    gridColumn: courtNum + 1,
                    gridRow: totalRows + 2,
                    borderTop: '1px solid #f0f0f0',
                    backgroundColor: 'transparent',
                    userSelect: 'none',
                  }}
                />
              )
            })}
            {showLastTimeLabelCol && (
              <TimeLabel
                rowIndex={totalRows}
                gridColumn={5}
                date={date}
                selection={selection}
                isDragging={isDragging}
                hoveredRow={hoveredRow}
              />
            )}
          </React.Fragment>

          {/* 3. Render Bookings */}
          {bookings.map((booking) => (
            <Booking booking={booking} onEdit={onEdit} onDelete={onDelete} isDragging={isDragging} />
          ))}
        </div>
      </Spin>
    </>
  )
}

export default CourtSchedule
